import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  TableBody,
  Grid,
  AccordionDetails,
  Typography,
  Accordion,
  AccordionSummary,
  TableRow,
  TableCell
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DealerContactModeListComponent from "./DealerContactModeListComponent";
import DealerContactDataListComponent from "./DealerContactDataListComponent";
import { isPhoneByCountry } from "utils/pure-validation/primitiveValidators";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CollapseCard } from "@totalsoft/rocket-ui";
import { changeValueInArray } from "utils/functions";
import {
  loadDealerContactModes,
  saveDealerContactModes,
  loadDealerContactInfos,
  saveDealerContactInfos
} from "../../actionCreators";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";
import { bindActionCreators } from "redux";
import { getDealersContactModes, getDealersContactInfos } from "features/partner/selectors";
import { connect } from "react-redux";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { items, shape, fromModel, email, required, stopOnFirstFailure, when } from "@totalsoft/pure-validations";
import { MinWidthTable } from "components/common/TableStyles";

export const contactModesValidator = (contactModeParams, defaultCountryCode) => {
  return items(
    fromModel(item =>
      shape({
        contactModeId: required,
        contactInfo:
          [
            email |> when(item.contactModeId === contactModeParams.contactModeEmail),
            isPhoneByCountry(defaultCountryCode)
              |> when(
                item.contactModeId === contactModeParams.contactModeMobilePhone ||
                  item.contactModeId === contactModeParams.contactModePhone
              )
          ] |> stopOnFirstFailure
      })
    )
  );
};

export const contactInfosValidator = defaultCountryCode => {
  return items(
    fromModel(() =>
      shape({
        email: email,
        phone: isPhoneByCountry(defaultCountryCode)
      })
    )
  );
};

export const DealerListComponent = ({
  dealer,
  contactModes,
  dealerContactModes,
  dealerContactModesLoaded,
  partnerId,
  onContactModeCardExpand,
  contactModeCardExpanded,
  onContactInfoCardExpand,
  contactInfoCardExpanded,
  actions,
  setIsDirty,
  dealerContactInfos,
  dealerContactInfosLoaded,
  params
}) => {
  const { t } = useTranslation();

  const [cardExpanded, setExpanded] = useState(false);
  const [dealerContactModesList, setDealerContactModesList] = useState(dealerContactModes || []);
  const [dealerContactInfosList, setDealerContactInfosList] = useState(dealerContactInfos || []);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const [saveContactModeDisabled, setSaveContactModeDisabled] = useState(true);
  const [saveContactInfoDisabled, setSaveContactInfoDisabled] = useState(true);
  const [validDefaultEmail, setValidDefaultEmail] = useState(true);
  const [validDefaultMobilePhone, setValidDefaultMobilePhone] = useState(true);
  const [validDefaultPhone, setValidDefaultPhone] = useState(true);

  const validatorContactModes = useMemo(() => contactModesValidator(params, params.defaultCountryCode), [params]);

  const [dealerContactModesValidation, validateDealerContactModes] = useDirtyFieldValidation(validatorContactModes);
  const validatorContactInfos = useMemo(
    () => contactInfosValidator(params.defaultCountryCode),
    [params.defaultCountryCode]
  );

  const [contactInfosValidation, validateContactInfos] = useDirtyFieldValidation(validatorContactInfos);
  useEffect(() => {
    if (contactModeCardExpanded && !dealerContactModesLoaded)
      actions.loadDealerContactModes(partnerId, dealer.dealerId);
  }, [contactModeCardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dealerContactModesLoaded) {
      setDealerContactModesList(dealerContactModes);
    }
  }, [dealerContactModesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const onExpand = expanded => {
    if (cardExpanded !== expanded) setExpanded(expanded);
  };

  const onContactModePropertyChange = idx => prop => value => {
    setDirtyInfo(`${idx}.${prop}`);
    setDealerContactModesList(
      Object.assign([], {
        ...dealerContactModesList,
        [idx]: {
          ...dealerContactModesList[idx],
          [prop]: value
        }
      })
    );
    setIsDirty(true);
    setValidDefaultEmail(true);
    setValidDefaultMobilePhone(true);
    setValidDefaultPhone(true);
    setSaveContactModeDisabled(false);
  };

  const addContactMode = (partnerId, dealerId) => {
    let contactModeList = [...dealerContactModesList, { dealerId }];
    setDealerContactModesList(contactModeList);
    setIsDirty(true);
    setSaveContactModeDisabled(false);
    setValidDefaultEmail(true);
    setValidDefaultMobilePhone(true);
    setValidDefaultPhone(true);
  };

  const saveContactModes = (partnerId, dealerContactModesList) => {
    if (!validateDealerContactModes(dealerContactModesList)) {
      return;
    }
    const counterDefaultEmail = validateUniqueDefaultContactModes_Email(dealerContactModesList);
    const counterDefaultMobilePhone = validateUniqueDefaultContactModes_MobilePhone(dealerContactModesList);
    const counterDefaultPhone = validateUniqueDefaultContactModes_Phone(dealerContactModesList);
    if (counterDefaultEmail.length > 1) {
      setValidDefaultEmail(false);
    } else if (counterDefaultMobilePhone.length > 1) {
      setValidDefaultMobilePhone(false);
    } else if (counterDefaultPhone.length > 1) {
      setValidDefaultPhone(false);
    } else {
      actions.saveDealerContactModes(partnerId, dealer.dealerId, dealerContactModesList, () => {
        setIsDirty(false);
      });
      setSaveContactModeDisabled(false);
    }
  };

  useEffect(() => {
    if (contactInfoCardExpanded && !dealerContactInfosLoaded)
      actions.loadDealerContactInfos(partnerId, dealer.dealerId);
  }, [contactInfoCardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dealerContactInfosLoaded) {
      setDealerContactInfosList(dealerContactInfos);
    }
  }, [dealerContactInfosLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const onContactInfoPropertyChange = idx => prop => value => {
    setDirtyInfo(`${idx}.${prop}`);
    setDealerContactInfosList(
      Object.assign([], {
        ...dealerContactInfosList,
        [idx]: {
          ...dealerContactInfosList[idx],
          [prop]: value
        }
      })
    );
    setIsDirty(true);
    setSaveContactInfoDisabled(false);
  };

  const addContactInfo = (partnerId, dealerId) => {
    let contactInfoList = [...dealerContactInfosList, { dealerId }];
    setDealerContactInfosList(contactInfoList);
    setSaveContactInfoDisabled(false);
    setIsDirty(true);
  };

  const saveContactInfos = (partnerId, dealerContactInfosList) => {
    if (!validateContactInfos(dealerContactInfosList)) {
      return;
    }
    actions.saveDealerContactInfos(partnerId, dealer.dealerId, dealerContactInfosList, () => {
      setIsDirty(false);
    });
    setSaveContactInfoDisabled(true);
  };

  const validateUniqueDefaultContactModes_Email = dealerContactModesList => {
    return dealerContactModesList.filter(
      contactMode => contactMode.isDefault === true && contactMode.contactModeId === params.contactModeEmail
    );
  };

  const validateUniqueDefaultContactModes_MobilePhone = dealerContactModesList => {
    return dealerContactModesList.filter(
      contactMode => contactMode.isDefault === true && contactMode.contactModeId === params.contactModeMobilePhone
    );
  };
  const validateUniqueDefaultContactModes_Phone = dealerContactModesList => {
    return dealerContactModesList.filter(
      contactMode => contactMode.isDefault === true && contactMode.contactModeId === params.contactModePhone
    );
  };

  useEffect(() => {
    validateDealerContactModes(dealerContactModesList, dirtyInfo);
  }, [dealerContactModesList, dirtyInfo, validateDealerContactModes]);

  useEffect(() => {
    validateContactInfos(dealerContactInfosList, dirtyInfo);
  }, [dealerContactInfosList, dirtyInfo, validateContactInfos]);

  const onRowSelectionChanged = (id, checked) => {
    let listWithSelection = changeValueInArray(dealerContactInfosList, "id", id, "isDefault", checked);
    setDealerContactInfosList(listWithSelection);
    dealerContactInfosList.forEach(function (item) {
      let computedList = changeValueInArray(listWithSelection, "id", item.id, "isDefault", id === item.id && checked);
      setDealerContactInfosList(computedList);
      listWithSelection = computedList;
      setSaveContactInfoDisabled(false);
    });
  };

  return (
    <Grid container sx={{ overflowX: "auto" }}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <MinWidthTable>
          <TableBody>
            <TableRow>
              <TableCell key={dealer.dealerId} sx={{ py: 0, border: "none" }}>
                <CollapseCard
                  id="propertyCard"
                  icon={DescriptionIcon}
                  iconColor="primary"
                  title={
                    <>
                      <span>{t("Partner.ContactInfo")}</span>{" "}
                      <span style={{ textTransform: "lowercase" }}>{t("Partner.OwnedBy")} </span>
                      {dealer.dealerName}
                    </>
                  }
                  onToggle={(_, expanded) => onExpand(!expanded)}
                  expanded={cardExpanded}
                  toggleOnHeaderClick
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Accordion
                        id="contactModeCard"
                        defaultExpanded
                        expanded={contactModeCardExpanded}
                        onChange={(_, expanded) => onContactModeCardExpand(_, expanded)}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6" color={"initial"} style={{ fontSize: "1.25rem" }}>
                            {t("Partner.PhoneAndEmail")}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <DealerContactModeListComponent
                            dealer={dealer}
                            contactModes={contactModes}
                            dealerContactModesList={dealerContactModesList}
                            addContactMode={addContactMode}
                            saveContactModes={saveContactModes}
                            partnerId={partnerId}
                            dealerId={dealer.id}
                            dealerContactModes={dealerContactModes}
                            onPropertyChange={onContactModePropertyChange}
                            saveContactModeDisabled={saveContactModeDisabled}
                            setSaveContactModeDisabled={setSaveContactModeDisabled}
                            validDefaultEmail={validDefaultEmail}
                            validDefaultMobilePhone={validDefaultMobilePhone}
                            validDefaultPhone={validDefaultPhone}
                            validation={dealerContactModesValidation}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion
                        id="contactModeCard"
                        defaultExpanded
                        expanded={contactInfoCardExpanded}
                        onChange={(_, expanded) => onContactInfoCardExpand(_, expanded)}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6" color={"initial"} style={{ fontSize: "1.25rem" }}>
                            {t("Partner.ContactPerson")}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <DealerContactDataListComponent
                            dealer={dealer}
                            contactModes={contactModes}
                            addContactInfo={addContactInfo}
                            partnerId={partnerId}
                            dealerContactInfos={dealerContactInfos}
                            onPropertyChange={onContactInfoPropertyChange}
                            dealerContactInfosList={dealerContactInfosList}
                            dealerId={dealer.id}
                            saveContactInfos={saveContactInfos}
                            saveContactInfoDisabled={saveContactInfoDisabled}
                            setSaveContactInfoDisabled={setSaveContactInfoDisabled}
                            validation={contactInfosValidation}
                            onRowSelectionChanged={onRowSelectionChanged}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </CollapseCard>
              </TableCell>
            </TableRow>
          </TableBody>
        </MinWidthTable>
      </Grid>
    </Grid>
  );
};

DealerListComponent.propTypes = {
  setIsDirty: PropTypes.func,
  dealer: PropTypes.object.isRequired,
  dealersList: PropTypes.array,
  contactModes: PropTypes.array.isRequired,
  dealerContactModes: PropTypes.array,
  dealerContactInfos: PropTypes.array,
  partnerId: PropTypes.number,
  dealerId: PropTypes.number,
  onContactModeCardExpand: PropTypes.func.isRequired,
  contactModeCardExpanded: PropTypes.bool,
  onContactInfoCardExpand: PropTypes.func.isRequired,
  contactInfoCardExpanded: PropTypes.bool,
  dealerContactModesList: PropTypes.array,
  actions: PropTypes.object.isRequired,
  dealerContactModesLoading: PropTypes.bool,
  dealerContactModesLoaded: PropTypes.bool,
  dealerContactInfosLoading: PropTypes.bool,
  dealerContactInfosLoaded: PropTypes.bool,
  contactModeParams: PropTypes.object,
  params: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const {
    content: dealerContactModes,
    loaded: dealerContactModesLoaded,
    loading: dealerContactModesLoading
  } = getDealersContactModes(state, ownProps.partnerId, ownProps.dealer.dealerId);

  const {
    content: dealerContactInfos,
    loaded: dealerContactInfosLoaded,
    loading: dealerContactInfosLoading
  } = getDealersContactInfos(state, ownProps.partnerId, ownProps.dealer.dealerId);

  return {
    dealerContactModes,
    dealerContactModesLoading,
    dealerContactModesLoaded,
    dealerContactInfos,
    dealerContactInfosLoading,
    dealerContactInfosLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDealerContactModes,
        saveDealerContactModes,
        loadDealerContactInfos,
        saveDealerContactInfos
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DealerListComponent);
